import { MTextField, ResponsiveFormContainer } from "@app/components";
import { MODES_OPTIONS } from "@app/types/generics.types";
import { toExponentialUppercase } from "@app/utils/functions";
import { Grid, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";

interface Fe55UncertaintyProps {
  mode: MODES_OPTIONS;
}

const Fe55Uncertainty = ({ mode }: Fe55UncertaintyProps) => {
  const form = useFormContext();
  const isView = mode === MODES_OPTIONS.VIEW;

  const calculateValues = (
    inputName: string,
    outputNames: Record<string, string>
  ) => {
    const inputValue = form.watch(inputName);

    if (inputValue !== undefined && inputValue !== null) {
      const k2_m3 = inputValue * 1e-6;
      const k1_m3_2 = Math.pow(k2_m3 / 2, 2);
      const k1_l_kg = Math.pow((inputValue * 1e-3) / 2, 2);
      const k1_g_2 = Math.pow(inputValue / 2, 2);

      form.setValue(outputNames.k2_m3, toExponentialUppercase(k2_m3, 1));
      form.setValue(outputNames.k1_m3_2, toExponentialUppercase(k1_m3_2, 4));
      form.setValue(outputNames.k1_l_kg, toExponentialUppercase(k1_l_kg, 4));
      form.setValue(outputNames.k1_g_2, toExponentialUppercase(k1_g_2, 4));
    }
  };

  const calculateSolid5mlValues = () => {
    const k2_mg = form.watch("mass_solid_minor_200g_k2_mg");

    if (k2_mg !== undefined && k2_mg !== null) {
      const k2_kg = k2_mg * 1e-6;
      form.setValue(
        "mass_solid_minor_200g_k2_kg",
        toExponentialUppercase(k2_kg, 2)
      );

      const k2_half = k2_kg / 2;
      const result = Math.pow(k2_half, 2) + Math.pow(k2_half, 2);

      form.setValue(
        "mass_solid_minor_200g_k1_kg_2",
        toExponentialUppercase(result, 4)
      );
      const scaledValue = (k2_mg * 1e-3) / 2;
      const resultmass = Math.pow(scaledValue, 2) + Math.pow(scaledValue, 2);

      form.setValue(
        "mass_solid_minor_200g_k1_g_2",
        toExponentialUppercase(resultmass, 4)
      );
    }
  };

  const calculateSolid200gValues = () => {
    const k2_mg = form.watch("mass_solid_200g_k2_mg");

    if (k2_mg !== undefined && k2_mg !== null) {
      const k2_kg = k2_mg * 1e-3;
      form.setValue("mass_solid_200g_k2_kg", toExponentialUppercase(k2_kg, 2));

      const k2_half = k2_kg / 2;
      const result = Math.pow(k2_half, 2) + Math.pow(k2_half, 2);

      form.setValue(
        "mass_solid_200g_k1_kg_2",
        toExponentialUppercase(result, 4)
      );
      const scaledValue = k2_mg / 2;
      const resultmass = Math.pow(scaledValue, 2) + Math.pow(scaledValue, 2);

      form.setValue(
        "mass_solid_200g_k1_g_2",
        toExponentialUppercase(resultmass, 4)
      );
    }
  };

  useEffect(() => {
    calculateValues("volume_water_1ml_k2_ml", {
      k2_m3: "volume_water_1ml_k2_m3",
      k1_m3_2: "volume_water_1ml_k1_m3_2",
      k1_l_kg: "volume_water_1ml_k1_l_kg",
      k1_g_2: "volume_water_1ml_k1_g_2",
    });

    calculateValues("volume_water_5ml_k2_ml", {
      k2_m3: "volume_water_5ml_k2_m3",
      k1_m3_2: "volume_water_5ml_k1_m3_2",
      k1_l_kg: "volume_water_5ml_k1_l_kg",
      k1_g_2: "volume_water_5ml_k1_g_2",
    });

    calculateValues("volume_water_25ml_k2_ml", {
      k2_m3: "volume_water_25ml_k2_m3",
      k1_m3_2: "volume_water_25ml_k1_m3_2",
      k1_l_kg: "volume_water_25ml_k1_l_kg",
      k1_g_2: "volume_water_25ml_k1_g_2",
    });

    calculateValues("volume_water_500ml_k2_ml", {
      k2_m3: "volume_water_500ml_k2_m3",
      k1_m3_2: "volume_water_500ml_k1_m3_2",
      k1_l_kg: "volume_water_500ml_k1_l_kg",
      k1_g_2: "volume_water_500ml_k1_g_2",
    });

    calculateValues("volume_sea_5ml_k2_ml", {
      k2_m3: "volume_sea_5ml_k2_m3",
      k1_m3_2: "volume_sea_5ml_k1_m3_2",
      k1_l_kg: "volume_sea_5ml_k1_l_kg",
      k1_g_2: "volume_sea_5ml_k1_g_2",
    });

    calculateSolid5mlValues();
    calculateSolid200gValues();
  }, [form.watch()]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Grid item xs={12} my={3}>
        <Typography variant="tagText">{"Muestra de agua"}</Typography>
      </Grid>
      <ResponsiveFormContainer columns={6}>
        <Typography variant="tagTextPressed" sx={{ whiteSpace: "pre-line" }}>
          {
            "Si vol 1 ml ≤vol<5 ml \n (*) Aplica la incertidumbre del material fungible volumétrico"
          }
        </Typography>
        <MTextField
          key={"volume_water_1ml_k2_ml"}
          name={"volume_water_1ml_k2_ml"}
          label={"Incert. Del volumen muestra para k=2 (ml)"}
          type="number"
          fullWidth
          validation={{ required: "El valor es requerido" }}
          disabled={isView}
        />
        <MTextField
          name={"volume_water_1ml_k2_m3"}
          label={"<span> k=2 (m<sup>3</sup>)</span>"}
          type="number"
          fullWidth
          disabled
        />
        <MTextField
          name={"volume_water_1ml_k1_m3_2"}
          label={"<span> k=1 u<sup>2</sup>(m<sup>3</sup>)<sup>2</sup></span>"}
          type="number"
          fullWidth
          disabled
        />
        <MTextField
          name={"volume_water_1ml_k1_l_kg"}
          label={
            "<span>k=1 u<sup>2</sup>(l)<sup>2</sup> y u<sup>2</sup>(kg)<sup>2</sup></span>"
          }
          type="number"
          fullWidth
          disabled
        />
        <MTextField
          name={"volume_water_1ml_k1_g_2"}
          label={"<span>k=1 u<sup>2</sup>(g)<sup>2</sup></span>"}
          type="number"
          fullWidth
          disabled
        />

        <Typography variant="tagTextPressed" sx={{ whiteSpace: "pre-line" }}>
          {
            "Si vol 5 ml ≤vol< 25 ml \n(*) Aplica la incertidumbre del material fungible volumétrico"
          }
        </Typography>
        <MTextField
          key={"volume_water_5ml_k2_ml"}
          name={"volume_water_5ml_k2_ml"}
          label={"Incert. Del volumen muestra para k=2 (ml)"}
          type="number"
          fullWidth
          validation={{ required: "El valor es requerido" }}
          disabled={isView}
        />
        <MTextField
          name={"volume_water_5ml_k2_m3"}
          label={"<span>k=2 (m<sup>3</sup>)</span>"}
          type="number"
          fullWidth
          disabled
        />
        <MTextField
          name={"volume_water_5ml_k1_m3_2"}
          label={"<span> k=1 u<sup>2</sup> (m<sup>3</sup>)<sup>2</sup></span>"}
          type="number"
          fullWidth
          disabled
        />
        <MTextField
          name={"volume_water_5ml_k1_l_kg"}
          label={
            "<span>k=1 u<sup>2</sup>(l)<sup>2</sup> y u<sup>2</sup>(kg)<sup>2</sup></span>"
          }
          type="number"
          fullWidth
          disabled
        />
        <MTextField
          name={"volume_water_5ml_k1_g_2"}
          label={"<span>k=1 u<sup>2</sup>(g)<sup>2</sup></span>"}
          type="number"
          fullWidth
          disabled
        />

        <Typography variant="tagTextPressed" sx={{ whiteSpace: "pre-line" }}>
          {
            "Si vol 25ml ≤ vol ≤ 500 ml \n(*) Aplica la incertidumbre del material fungible volumétrico"
          }
        </Typography>
        <MTextField
          key={"volume_water_25ml_k2_ml"}
          name={"volume_water_25ml_k2_ml"}
          label={"Incert. Del volumen muestra para k=2 (ml)"}
          type="number"
          fullWidth
          validation={{ required: "El valor es requerido" }}
          inputProps={{ step: 0.01 }}
          disabled={isView}
        />
        <MTextField
          name={"volume_water_25ml_k2_m3"}
          label={"<span>k=2 (m<sup>3</sup>)</span>"}
          type="number"
          fullWidth
          disabled
        />

        <MTextField
          name={"volume_water_25ml_k1_m3_2"}
          label={"<span>k=1 u<sup>2</sup> (m<sup>3</sup>)<sup>2</sup></span>"}
          type="number"
          fullWidth
          disabled
        />

        <MTextField
          name={"volume_water_25ml_k1_l_kg"}
          label={
            "<span>k=1 u<sup>2</sup>(l)<sup>2</sup> y u<sup>2</sup>(kg)<sup>2</sup></span>"
          }
          type="number"
          fullWidth
          disabled
        />

        <MTextField
          name={"volume_water_25ml_k1_g_2"}
          label={"<span>k=1 u<sup>2</sup>(g)<sup>2</sup></span>"}
          type="number"
          fullWidth
          disabled
        />

        <Typography variant="tagTextPressed" sx={{ whiteSpace: "pre-line" }}>
          {
            "Si vol > 500 ml \n(*) Aplica la incertidumbre del material fungible volumétrico"
          }
        </Typography>
        <MTextField
          key={"volume_water_500ml_k2_ml"}
          name={"volume_water_500ml_k2_ml"}
          label={"Incert. Del volumen muestra para k=2 (ml)"}
          type="number"
          fullWidth
          validation={{ required: "El valor es requerido" }}
          disabled={isView}
        />
        <MTextField
          name={"volume_water_500ml_k2_m3"}
          label={"<span>k=2 (m<sup>3</sup>)</span>"}
          type="number"
          fullWidth
          disabled
        />

        <MTextField
          name={"volume_water_500ml_k1_m3_2"}
          label={"<span>k=1 u<sup>2</sup> (m<sup>3</sup>)<sup>2</sup></span>"}
          type="number"
          fullWidth
          disabled
        />

        <MTextField
          name={"volume_water_500ml_k1_l_kg"}
          label={
            "<span>k=1 u<sup>2</sup>(l)<sup>2</sup> y u<sup>2</sup>(kg)<sup>2</sup></span>"
          }
          type="number"
          fullWidth
          disabled
        />

        <MTextField
          name={"volume_water_500ml_k1_g_2"}
          label={"<span>k=1 u<sup>2</sup>(g)<sup>2</sup></span>"}
          type="number"
          fullWidth
          disabled
        />
      </ResponsiveFormContainer>

      <Grid item xs={12} my={3}>
        <Typography variant="tagText">{"Muestra de agua de mar"}</Typography>
      </Grid>
      <ResponsiveFormContainer columns={6}>
        <Typography variant="tagTextPressed" sx={{ whiteSpace: "pre-line" }}>
          {
            "Si vol = 5 ml \n(*) Aplica la incertidumbre del material fungible volumétrico"
          }
        </Typography>
        <MTextField
          key={"volume_sea_5ml_k2_ml"}
          name={"volume_sea_5ml_k2_ml"}
          label={"Incert. Del volumen muestra para k=2 (ml)"}
          type="number"
          fullWidth
          validation={{ required: "El valor es requerido" }}
          disabled={isView}
        />
        <MTextField
          key={"volume_sea_5ml_k2_m3"}
          name={"volume_sea_5ml_k2_m3"}
          label={"<span>k=2 (m<sup>3</sup>)</span>"}
          type="text"
          fullWidth
          disabled
        />

        <MTextField
          key={"volume_sea_5ml_k1_m3_2"}
          name={"volume_sea_5ml_k1_m3_2"}
          label={"<span>k=1 u<sup>2</sup> (m<sup>3</sup>)<sup>2</sup></span>"}
          type="text"
          fullWidth
          disabled
        />

        <MTextField
          key={"volume_sea_5ml_k1_l_kg"}
          name={"volume_sea_5ml_k1_l_kg"}
          label={
            "<span>k=1 u<sup>2</sup>(l)<sup>2</sup> y u<sup>2</sup>(kg)<sup>2</sup></span>"
          }
          type="text"
          fullWidth
          disabled
        />

        <MTextField
          name={"volume_sea_5ml_k1_g_2"}
          label={"<span>k=1 u<sup>2</sup>(g)<sup>2</sup></span>"}
          type="text"
          fullWidth
          disabled
        />
      </ResponsiveFormContainer>

      <Grid item xs={12} my={3}>
        <Typography variant="tagText">
          {"Muestras sólidas (no aguas)"}
        </Typography>
      </Grid>
      <ResponsiveFormContainer columns={6}>
        <Typography variant="tagTextPressed" sx={{ whiteSpace: "pre-line" }}>
          {
            "Si masa ≤ 200g \n(*) Aplica la incertidumbre máxima de la balanza analítica con 200g"
          }
        </Typography>
        <MTextField
          key={"mass_solid_minor_200g_k2_mg"}
          name={"mass_solid_minor_200g_k2_mg"}
          label={"Incert. masa muestra para k=2 (mg)"}
          type="number"
          fullWidth
          validation={{ required: "El valor es requerido" }}
          inputProps={{ step: 0.01 }}
          disabled={isView}
        />
        <MTextField
          name={"mass_solid_minor_200g_k2_kg"}
          label={"<span>k=2 (kg)</span>"}
          type="number"
          fullWidth
          disabled
        />

        <MTextField
          name={"mass_solid_minor_200g_k1_kg_2"}
          label={"<span>k=1 u<sup>2</sup> (kg)<sup>2</sup></span>"}
          type="number"
          fullWidth
          disabled
        />

        <MTextField
          name={"mass_solid_minor_200g_k1_g_2"}
          label={"<span>k=1 u<sup>2</sup>(g)<sup>2</sup></span>"}
          type="number"
          fullWidth
          disabled
        />
      </ResponsiveFormContainer>
      <ResponsiveFormContainer columns={6} mt={1}>
        <Typography variant="tagTextPressed" sx={{ whiteSpace: "pre-line" }}>
          {
            "Si masa > 200g \n(*)Aplica la incertidumbre máxima de la granatario con 2000g"
          }
        </Typography>
        <MTextField
          key={"mass_solid_200g_k2_mg"}
          name={"mass_solid_200g_k2_mg"}
          label={"Incert. masa muestra para k=2 (mg)"}
          type="number"
          fullWidth
          validation={{ required: "El valor es requerido" }}
          disabled={isView}
        />

        <MTextField
          name={"mass_solid_200g_k2_kg"}
          label={"<span> k=2 (kg)</span>"}
          type="number"
          fullWidth
          disabled
        />

        <MTextField
          name={"mass_solid_200g_k1_kg_2"}
          label={"<span>k=1 u<sup>2</sup> (kg)<sup>2</sup></span>"}
          type="number"
          fullWidth
          disabled
        />

        <MTextField
          name={"mass_solid_200g_k1_g_2"}
          label={"<span>k=1 u<sup>2</sup>(g)<sup>2</sup></span>"}
          type="number"
          fullWidth
          disabled
        />
      </ResponsiveFormContainer>
    </>
  );
};

export default Fe55Uncertainty;
